import { useCallback, useEffect, useState } from 'react';
import type { Tarea } from '../types';
import { storage } from '../lib/storage';
import { cancelarNotificacionDeTarea, programarNotificacionTarea } from '../lib/notifications';
import { encolar } from '../lib/outbox';
import { onDatosCambiados } from '../lib/events';

function extraerCamposParaSync(tarea: Tarea): Record<string, unknown> {
  return {
    titulo: tarea.titulo,
    fechaLimite: tarea.fechaLimite ?? '',
    notas: tarea.notas,
    hecha: tarea.hecha,
  };
}

export function useTareas() {
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(async () => {
    const lista = await storage.obtenerTareas();
    setTareas(lista);
  }, []);

  useEffect(() => {
    void (async () => {
      await recargar();
      setCargando(false);
    })();
    return onDatosCambiados(() => {
      void recargar();
    });
  }, [recargar]);

  const guardarTarea = useCallback(
    async (tarea: Tarea, esCreate: boolean) => {
      const ahora = new Date().toISOString();
      const conTimestamp: Tarea = { ...tarea, updatedAt: ahora };
      await storage.guardarTarea(conTimestamp);
      await encolar({
        entity: 'tarea',
        id: tarea.id,
        type: esCreate ? 'create' : 'update',
        data: extraerCamposParaSync(conTimestamp),
        clientUpdatedAt: ahora,
      });
      void programarNotificacionTarea(conTimestamp);
      await recargar();
    },
    [recargar]
  );

  // Marcar hecha / pendiente: cambia el estado pero conserva la tarea (el usuario
  // a veces reutiliza las notas). Borrar es una acción separada (eliminarTarea).
  const alternarHecha = useCallback(
    async (tarea: Tarea) => {
      const ahora = new Date().toISOString();
      const actualizada: Tarea = { ...tarea, hecha: !tarea.hecha, updatedAt: ahora };
      await storage.guardarTarea(actualizada);
      await encolar({
        entity: 'tarea',
        id: tarea.id,
        type: 'update',
        data: { hecha: actualizada.hecha },
        clientUpdatedAt: ahora,
      });
      // Si quedó hecha, cancelamos su recordatorio; si volvió a pendiente y tiene
      // fecha, lo reprogramamos.
      void programarNotificacionTarea(actualizada, { silencioso: true });
      await recargar();
    },
    [recargar]
  );

  const eliminarTarea = useCallback(
    async (id: string) => {
      try {
        await cancelarNotificacionDeTarea(id);
      } catch (error) {
        console.error('Error al cancelar notificación de tarea:', error);
      }
      await storage.eliminarTarea(id);
      await encolar({
        entity: 'tarea',
        id,
        type: 'delete',
        clientUpdatedAt: new Date().toISOString(),
      });
      await recargar();
    },
    [recargar]
  );

  const tareasPendientes = tareas.filter((t) => !t.hecha);
  const tareasHechas = tareas.filter((t) => t.hecha);

  return {
    tareas,
    tareasPendientes,
    tareasHechas,
    cargando,
    guardarTarea,
    alternarHecha,
    eliminarTarea,
  };
}
